import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import AdvancedDataTable from '../../components/AdvancedDataTable';
import ViewTabs from '../../components/crm/ViewTabs';
import ViewToolbar from '../../components/crm/ViewToolbar';
import ViewEditorModal from '../../components/crm/ViewEditorModal';
import CreateRecordModal from '../../components/crm/CreateRecordModal';
import KanbanBoard from '../../components/crm/KanbanBoard';
import useObjectView from '../../hooks/useObjectView';
import { useDataObject } from '../DataObjectSlugResolver';

export default function AbleCRMObjectList() {
  const dataObject = useDataObject();
  const objectDefinitionId = dataObject?.id;
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const {
    views, activeView, activeViewId, entries, tableColumns, loading,
    query, setQuery, currentPage, setCurrentPage, pageSize, totalItems,
    sortBy, sortOrder, showFilterModal, setShowFilterModal, filterMode, editingView,
    availableFields, fieldDefMap, fetchEntries,
    handleSort, handlePageSizeChange, handleSelectView,
    handleCreateView, handleEditView, handleSaveView, handleDeleteView,
    handleInlineColumnsChange, handleInlineFiltersChange, handleDuplicateView,
    handleColumnResize, handleReorderViews, handleTogglePin,
    isKanban, kanbanColumns, kanbanGroupedEntries, kanbanDisplayFields, handleMoveRecord,
  } = useObjectView(objectDefinitionId);

  // Derive object info from the active view
  const objectDef = activeView?.object_definition;
  const objectName = objectDef?.name || 'Records';

  const handleRowClick = (row) => {
    const slug = dataObject?.slug_plural || dataObject?.slug;
    navigate(`/data/${slug}/${row.id}`);
  };

  const handleRecordCreated = () => {
    fetchEntries();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          {objectName}{objectName.endsWith('s') ? '' : 's'}
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/data/${dataObject?.slug_plural || dataObject?.slug}/import`)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New {objectDef?.name || 'Record'}
          </button>
        </div>
      </div>

      {/* View Tabs */}
      <ViewTabs
        views={views}
        activeViewId={activeViewId}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onEditView={handleEditView}
        onDeleteView={handleDeleteView}
        onReorderViews={handleReorderViews}
        onTogglePin={handleTogglePin}
      />

      {/* Toolbar */}
      <ViewToolbar
        query={query}
        setQuery={setQuery}
        objectName={objectName}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        activeView={activeView}
        availableFields={availableFields}
        isKanban={isKanban}
        onColumnsChange={handleInlineColumnsChange}
        onFiltersChange={handleInlineFiltersChange}
        onEditView={() => handleEditView(activeView)}
        onDuplicateView={handleDuplicateView}
        onDeleteView={() => activeView && handleDeleteView(activeView.id)}
      />

      {/* Data Table or Kanban */}
      {isKanban ? (
        <KanbanBoard
          columns={kanbanColumns}
          groupedEntries={kanbanGroupedEntries}
          onMoveRecord={handleMoveRecord}
          onRecordClick={handleRowClick}
          displayFields={kanbanDisplayFields}
          fieldDefMap={fieldDefMap}
        />
      ) : (
        <AdvancedDataTable
          columns={tableColumns}
          data={entries}
          totalItems={totalItems}
          loading={loading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          onRowClick={handleRowClick}
          onColumnResize={handleColumnResize}
          entityName={objectName.toLowerCase()}
          emptyState={
            <EmptyState
              illustration="crm"
              headline={`No ${objectName.toLowerCase()} yet`}
              description="Add your first record to start organizing and managing your data."
              buttonLabel={`New ${objectDef?.name || 'Record'}`}
              onAction={() => setShowCreateModal(true)}
            />
          }
        />
      )}

      {/* View Editor Modal */}
      <ViewEditorModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onSave={handleSaveView}
        mode={filterMode}
        initialView={editingView || activeView}
        availableFields={availableFields}
      />

      <CreateRecordModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleRecordCreated}
        objectDefinitionId={objectDefinitionId}
        fieldDefinitions={availableFields}
      />
    </div>
  );
}
